import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../shadcn/hover-card";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../shadcn/tooltip";
import { Badge, type BadgeProps } from "../../shadcn/badge";

type StatusKey = "connected" | "reconnect" | "expired" | "invalid" | "disabled";

const STATUS_MAP: Record<string, StatusKey> = {
  active: "connected",
  needs_reconnect: "reconnect",
  expired: "expired",
  invalid: "invalid",
  disabled: "disabled",
};

const STATUS_VARIANTS: Record<
  StatusKey,
  {
    label: string;
    variant: BadgeProps["variant"];
    icon: React.ReactNode;
  }
> = {
  connected: {
    label: "Connected",
    variant: "success",
    icon: <span className="size-1.5 rounded-full bg-green-600" />,
  },
  reconnect: {
    label: "Reconnect needed",
    variant: "warning",
    icon: <RefreshCw className="size-3" />,
  },
  expired: {
    label: "Expired",
    variant: "destructive",
    icon: <AlertTriangle className="size-3" />,
  },
  invalid: {
    label: "Invalid",
    variant: "destructive",
    icon: <AlertTriangle className="size-3" />,
  },
  disabled: {
    label: "Disabled",
    variant: "color-slate",
    icon: <AlertTriangle className="size-3" />,
  },
};

export function CredentialStatusBadge({
  status,
  validationError,
}: {
  status: string | null;
  validationError?: string;
}) {
  const key: StatusKey = STATUS_MAP[status ?? "active"] ?? "connected";
  const config = STATUS_VARIANTS[key];

  const badge = (
    <Badge variant={config.variant} ellipsis>
      {config.icon}
      <span data-label>{config.label}</span>
    </Badge>
  );

  if (key === "disabled") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">{badge}</span>
        </TooltipTrigger>
        <TooltipContent>
          This integration has been disabled by an admin. Contact your
          administrator to re-enable it.
        </TooltipContent>
      </Tooltip>
    );
  }

  if (!validationError) return badge;

  return (
    <HoverCard openDelay={100} closeDelay={0}>
      <HoverCardTrigger asChild>
        <span className="cursor-default">{badge}</span>
      </HoverCardTrigger>
      <HoverCardContent
        side="bottom"
        align="start"
        className="w-auto max-w-100 p-4 flex flex-col gap-4"
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-destructive shrink-0" />
          <span className="text-xs font-medium text-destructive">
            Validation failed
          </span>
        </div>
        <p className="text-xs text-muted-foreground leading-4">
          {validationError}
        </p>
      </HoverCardContent>
    </HoverCard>
  );
}
